import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Crown, Sparkles, Palette, Wand2, LayoutGrid, BarChart3, Zap,
  Loader2, Check, ArrowRight, Play, Image as ImageIcon, Video, Music,
} from 'lucide-react';

const PRESET_GRADIENTS = [
  { name: 'Neon Nights', value: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)' },
  { name: 'Sunset', value: 'linear-gradient(135deg, #F97316 0%, #DB2777 100%)' },
  { name: 'Ocean', value: 'linear-gradient(135deg, #0EA5E9 0%, #6366F1 100%)' },
  { name: 'Emerald', value: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' },
  { name: 'Midnight', value: 'linear-gradient(135deg, #1E293B 0%, #4C1D95 100%)' },
  { name: 'Fire', value: 'linear-gradient(135deg, #DC2626 0%, #F59E0B 100%)' },
  { name: 'Aurora', value: 'linear-gradient(135deg, #06B6D4 0%, #A855F7 60%, #EC4899 100%)' },
  { name: 'Cyberpunk', value: 'linear-gradient(135deg, #F0ABFC 0%, #6366F1 100%)' },
];

const HERO_STYLES = [
  { key: 'large', label: 'Large hero', desc: 'Massive banner + big artwork' },
  { key: 'cinematic', label: 'Cinematic', desc: 'Full-bleed video/photo experience' },
  { key: 'minimal', label: 'Minimal', desc: 'Compact, focus on content' },
];

const FEATURES = [
  { icon: Palette, title: 'Custom Backgrounds', desc: 'Gradients, images, or hero videos' },
  { icon: LayoutGrid, title: 'Drag-and-drop Layout', desc: 'Rearrange widgets & sections (soon)' },
  { icon: Wand2, title: 'AI Profile Designer', desc: 'Auto-generate a look from a vibe (soon)' },
  { icon: Sparkles, title: 'Premium Themes', desc: '16 exclusive templates' },
  { icon: BarChart3, title: 'Advanced Analytics', desc: 'Deeper engagement insights' },
  { icon: Zap, title: 'Priority Support', desc: 'Faster response from the DMV team' },
];

function LockedOverlay({ onUpgrade }) {
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/70 backdrop-blur-sm rounded-2xl">
      <div className="text-center px-6">
        <div className="w-12 h-12 mx-auto rounded-2xl bg-gradient-to-br from-amber-400 to-fuchsia-500 flex items-center justify-center shadow-lg mb-3">
          <Crown className="w-6 h-6 text-white" />
        </div>
        <h3 className="text-lg font-black uppercase tracking-tight">Studio+ Required</h3>
        <p className="text-xs text-muted-foreground mb-4 max-w-xs mx-auto">
          Activate Creator Studio+ to save your custom profile look.
        </p>
        <Button
          onClick={onUpgrade}
          className="bg-gradient-to-r from-amber-500 to-fuchsia-500 hover:from-amber-600 hover:to-fuchsia-600"
          data-testid="studio-plus-locked-upgrade"
        >
          Upgrade to Studio+ <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </div>
    </div>
  );
}

export default function CreatorStudioPlusPage() {
  const { getAuthHeaders, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [status, setStatus] = useState({ is_studio_plus: false, studio_plus_until: null });
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [sRes, pRes] = await Promise.all([
        fetch(`${API}/studio-plus/status`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/studio-plus/profile-settings`, { headers }).then(r => r.ok ? r.json() : null),
      ]);
      if (sRes) setStatus(sRes);
      if (pRes) setSettings(pRes.settings);
    } catch { /* noop */ }
    setLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    loadAll();
  }, [authLoading, isAuthenticated, loadAll, navigate]);

  const activate = async (plan = 'monthly') => {
    setActivating(true);
    try {
      const res = await fetch(`${API}/studio-plus-billing/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ plan, origin_url: window.location.origin }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      window.location.href = data.checkout_url;
    } catch {
      toast.error('Could not start checkout');
      setActivating(false);
    }
  };

  const openPortal = async () => {
    try {
      const res = await fetch(`${API}/studio-plus-billing/portal`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      window.location.href = data.url;
    } catch {
      toast.error('Could not open billing portal');
    }
  };

  const cancel = async () => {
    if (!window.confirm('Manage or cancel your Studio+ subscription in Stripe billing portal?')) return;
    await openPortal();
  };

  const saveSettings = async () => {
    if (!status.is_studio_plus) {
      toast.error('Activate Studio+ first');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/studio-plus/profile-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ settings }),
      });
      if (!res.ok) throw new Error();
      toast.success('Profile settings saved');
    } catch {
      toast.error('Save failed');
    }
    setSaving(false);
  };

  const patchSettings = (path, value) => {
    setSettings((prev) => {
      const next = JSON.parse(JSON.stringify(prev || {}));
      const parts = path.split('.');
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = cur[parts[i]] || {};
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  if (loading || !settings) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const bg = settings.background || {};
  const hero = settings.hero || {};

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-400/15 via-fuchsia-500/15 to-violet-600/15" />
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-fuchsia-500 flex items-center justify-center shadow-xl">
              <Crown className="w-7 h-7 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl sm:text-4xl font-black tracking-tight" data-testid="studio-plus-title">
                  Creator Studio+
                </h1>
                {status.is_studio_plus && (
                  <span
                    className="text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                    data-testid="studio-plus-active-badge"
                  >
                    Active
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground max-w-2xl">
                The highest tier. Fully customize your profile, unlock advanced analytics, and access AI design tools.
              </p>
            </div>
          </div>
          {status.is_studio_plus && status.studio_plus_until && (
            <p className="text-xs text-muted-foreground mt-2" data-testid="studio-plus-until">
              Active until {new Date(status.studio_plus_until).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue={status.is_studio_plus ? 'customize' : 'overview'} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="overview" data-testid="studio-plus-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="customize" data-testid="studio-plus-tab-customize">Customize Profile</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            {/* Features grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-8">
              {FEATURES.map((f, i) => (
                <Card key={i} className="p-4 bg-card/60 border-border">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400/20 to-fuchsia-500/20 flex items-center justify-center mb-3">
                    <f.icon className="w-5 h-5 text-fuchsia-400" />
                  </div>
                  <h3 className="font-bold mb-1">{f.title}</h3>
                  <p className="text-xs text-muted-foreground">{f.desc}</p>
                </Card>
              ))}
            </div>

            {/* CTA */}
            <Card className="p-8 bg-gradient-to-br from-amber-500/10 via-fuchsia-500/10 to-violet-600/10 border-fuchsia-500/30">
              {status.is_studio_plus ? (
                <div className="text-center">
                  <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/15 flex items-center justify-center mb-3">
                    <Check className="w-7 h-7 text-emerald-400" />
                  </div>
                  <h2 className="text-2xl font-black uppercase mb-2">You&apos;re a Studio+ Creator</h2>
                  <p className="text-muted-foreground mb-6 text-sm">
                    Customize your profile in the next tab. All premium features are unlocked.
                  </p>
                  <Button
                    variant="outline"
                    onClick={cancel}
                    data-testid="studio-plus-cancel-btn"
                  >
                    Manage subscription
                  </Button>
                </div>
              ) : (
                <div className="text-center">
                  <h2 className="text-2xl sm:text-3xl font-black uppercase mb-2">Unlock the full studio</h2>
                  <p className="text-muted-foreground mb-6 text-sm max-w-xl mx-auto">
                    Ship a profile that actually reflects your brand. Custom backgrounds, hero videos,
                    animated gradients, deeper analytics, and priority support.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md mx-auto mb-4">
                    <button
                      onClick={() => activate('monthly')}
                      disabled={activating}
                      data-testid="studio-plus-plan-monthly"
                      className="p-4 rounded-2xl border-2 border-border hover:border-fuchsia-500 transition-all bg-card/60 group text-left"
                    >
                      <p className="text-[10px] uppercase font-black tracking-widest text-muted-foreground">Monthly</p>
                      <p className="text-3xl font-black">$9.99<span className="text-sm font-semibold text-muted-foreground">/mo</span></p>
                      <p className="text-xs text-muted-foreground mt-1">Flexible · cancel anytime</p>
                    </button>
                    <button
                      onClick={() => activate('yearly')}
                      disabled={activating}
                      data-testid="studio-plus-plan-yearly"
                      className="relative p-4 rounded-2xl border-2 border-fuchsia-500 bg-gradient-to-br from-amber-500/10 to-fuchsia-500/10 group text-left"
                    >
                      <span className="absolute -top-2.5 right-3 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-black uppercase tracking-widest">
                        Save $20
                      </span>
                      <p className="text-[10px] uppercase font-black tracking-widest text-fuchsia-400">Yearly</p>
                      <p className="text-3xl font-black">$99<span className="text-sm font-semibold text-muted-foreground">/yr</span></p>
                      <p className="text-xs text-muted-foreground mt-1">Best value · $8.25/mo</p>
                    </button>
                  </div>
                  {activating && (
                    <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> Redirecting to Stripe…
                    </div>
                  )}
                  <p className="text-[11px] text-muted-foreground mt-3">
                    Secure Stripe checkout. Cancel anytime from the billing portal.
                  </p>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="customize">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              {/* LEFT: controls */}
              <div className="lg:col-span-3 relative">
                {!status.is_studio_plus && (
                  <LockedOverlay onUpgrade={() => activate('monthly')} />
                )}
                <div className={`space-y-6 ${!status.is_studio_plus ? 'blur-[2px] pointer-events-none select-none' : ''}`}>
                  {/* Background */}
                  <Card className="p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <Palette className="w-4 h-4 text-fuchsia-400" />
                      <h3 className="font-bold uppercase tracking-tight text-sm">Background</h3>
                    </div>
                    <div className="flex gap-2 mb-4">
                      {['gradient', 'image', 'video'].map((t) => (
                        <button
                          key={t}
                          onClick={() => patchSettings('background.type', t)}
                          data-testid={`studio-plus-bg-type-${t}`}
                          className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide border ${
                            bg.type === t
                              ? 'bg-fuchsia-500 text-white border-fuchsia-500'
                              : 'border-border text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          {t === 'gradient' ? <><Palette className="w-3 h-3 inline mr-1" />Gradient</> :
                           t === 'image' ? <><ImageIcon className="w-3 h-3 inline mr-1" />Image</> :
                           <><Video className="w-3 h-3 inline mr-1" />Video</>}
                        </button>
                      ))}
                    </div>
                    {bg.type === 'gradient' && (
                      <div className="grid grid-cols-4 gap-2">
                        {PRESET_GRADIENTS.map((g) => (
                          <button
                            key={g.name}
                            onClick={() => patchSettings('background.value', g.value)}
                            data-testid={`studio-plus-gradient-${g.name}`}
                            className={`aspect-square rounded-xl border-2 transition-all ${
                              bg.value === g.value ? 'border-white scale-95' : 'border-transparent hover:scale-95'
                            }`}
                            style={{ background: g.value }}
                            title={g.name}
                          />
                        ))}
                      </div>
                    )}
                    {(bg.type === 'image' || bg.type === 'video') && (
                      <div className="space-y-2">
                        <Label className="text-xs">{bg.type === 'image' ? 'Image URL' : 'Video URL (mp4)'}</Label>
                        <Input
                          value={bg.value && !bg.value.startsWith('linear') ? bg.value : ''}
                          onChange={(e) => patchSettings('background.value', e.target.value)}
                          placeholder={bg.type === 'image' ? 'https://…/hero.jpg' : 'https://…/hero.mp4'}
                          data-testid="studio-plus-bg-url"
                        />
                        <p className="text-[11px] text-muted-foreground">
                          Paste a hosted URL. Upload UI coming soon.
                        </p>
                      </div>
                    )}
                  </Card>

                  {/* Colors */}
                  <Card className="p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <Sparkles className="w-4 h-4 text-violet-400" />
                      <h3 className="font-bold uppercase tracking-tight text-sm">Brand Colors</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs mb-1 block">Primary</Label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={settings.primary_color || '#8B5CF6'}
                            onChange={(e) => patchSettings('primary_color', e.target.value)}
                            className="w-10 h-10 rounded-lg cursor-pointer border border-border bg-transparent"
                            data-testid="studio-plus-primary-color"
                          />
                          <Input
                            value={settings.primary_color || ''}
                            onChange={(e) => patchSettings('primary_color', e.target.value)}
                            className="font-mono text-xs"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs mb-1 block">Accent</Label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={settings.accent_color || '#EC4899'}
                            onChange={(e) => patchSettings('accent_color', e.target.value)}
                            className="w-10 h-10 rounded-lg cursor-pointer border border-border bg-transparent"
                            data-testid="studio-plus-accent-color"
                          />
                          <Input
                            value={settings.accent_color || ''}
                            onChange={(e) => patchSettings('accent_color', e.target.value)}
                            className="font-mono text-xs"
                          />
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Hero */}
                  <Card className="p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <LayoutGrid className="w-4 h-4 text-amber-400" />
                      <h3 className="font-bold uppercase tracking-tight text-sm">Hero Style</h3>
                    </div>
                    <div className="space-y-2 mb-4">
                      {HERO_STYLES.map((h) => (
                        <button
                          key={h.key}
                          onClick={() => patchSettings('hero.style', h.key)}
                          data-testid={`studio-plus-hero-${h.key}`}
                          className={`w-full text-left p-3 rounded-xl border transition-all ${
                            hero.style === h.key
                              ? 'border-fuchsia-500 bg-fuchsia-500/5'
                              : 'border-border hover:border-fuchsia-500/40'
                          }`}
                        >
                          <p className="font-semibold text-sm">{h.label}</p>
                          <p className="text-xs text-muted-foreground">{h.desc}</p>
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-muted/30">
                      <div>
                        <p className="text-sm font-semibold flex items-center gap-1.5">
                          <Music className="w-3 h-3" />
                          Autoplay music
                        </p>
                        <p className="text-[11px] text-muted-foreground">Play featured track when visitors land on profile</p>
                      </div>
                      <Switch
                        checked={!!hero.autoplay_music}
                        onCheckedChange={(v) => patchSettings('hero.autoplay_music', v)}
                        data-testid="studio-plus-autoplay-toggle"
                      />
                    </div>
                  </Card>

                  {/* Save */}
                  <div className="sticky bottom-4">
                    <Button
                      size="lg"
                      onClick={saveSettings}
                      disabled={saving}
                      className="w-full bg-gradient-to-r from-fuchsia-500 to-violet-600 hover:from-fuchsia-600 hover:to-violet-700 shadow-lg"
                      data-testid="studio-plus-save-btn"
                    >
                      {saving ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                      ) : (
                        <><Check className="w-4 h-4 mr-2" /> Save profile settings</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* RIGHT: live preview */}
              <div className="lg:col-span-2">
                <div className="sticky top-4">
                  <p className="text-xs uppercase font-black tracking-widest text-muted-foreground mb-2">Live Preview</p>
                  <Card className="overflow-hidden">
                    <div
                      className="relative h-64"
                      style={
                        bg.type === 'gradient'
                          ? { background: bg.value }
                          : bg.type === 'image' && bg.value
                          ? { backgroundImage: `url(${bg.value})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                          : { background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)' }
                      }
                      data-testid="studio-plus-preview"
                    >
                      {bg.type === 'video' && bg.value ? (
                        <video src={bg.value} autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover" />
                      ) : null}
                      <div className="absolute inset-0 bg-black/40" />
                      <div className="absolute bottom-4 left-4 right-4 text-white">
                        <p className="text-[10px] uppercase tracking-widest opacity-70">Your name</p>
                        <p className="text-2xl font-black">Creator Profile</p>
                        <div className="flex items-center gap-2 mt-2">
                          <button
                            className="px-3 py-1.5 rounded-full text-xs font-bold text-black"
                            style={{ backgroundColor: settings.primary_color || '#8B5CF6' }}
                          >
                            <Play className="w-3 h-3 inline mr-1" /> Play latest
                          </button>
                          <button
                            className="px-3 py-1.5 rounded-full text-xs font-bold text-white border"
                            style={{ borderColor: settings.accent_color || '#EC4899' }}
                          >
                            Subscribe
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="p-3 text-xs text-muted-foreground">
                      This is how the top of your profile will look to visitors.
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
